// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "remind me about this message" — pending reminders for the current user.
// A reminder is about a Konvo message, an Outlook mail (the mail is not moved)
// or is a bare note.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import KonvoMail from '@app/features/konvo_mail/state/KonvoMail';
import {transitionTo} from '@app/features/navigation/utils/RouterUtils';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('KonvoReminders');

export type KonvoReminderSource = 'mail' | 'message' | 'note';

export interface KonvoReminder {
	id: string;
	/** Missing from servers older than mail reminders; read it through reminderSource(). */
	source?: KonvoReminderSource;
	/** The Graph message id when source is 'mail'. */
	mail_message_id?: string | null;
	channel_id: string;
	message_id: string;
	guild_id: string | null;
	author_name: string | null;
	snippet: string | null;
	note: string | null;
	remind_at: string;
	created_at: string;
}

/** Where a mail reminder keeps its Graph id on servers that only send channel_id / message_id. */
const MAIL_CHANNEL = 'mail';

export function reminderSource(reminder: KonvoReminder): KonvoReminderSource {
	if (reminder.source) return reminder.source;
	if (reminder.channel_id === MAIL_CHANNEL) return 'mail';
	return reminder.channel_id && reminder.message_id && reminder.message_id !== '0' ? 'message' : 'note';
}

/** The app path a reminder jumps to: the mail in Emails, the message, or null for a bare note. */
export function reminderPath(reminder: KonvoReminder): string | null {
	switch (reminderSource(reminder)) {
		case 'mail': {
			const id = reminder.mail_message_id ?? reminder.message_id;
			return id ? `/emails?open=${encodeURIComponent(id)}` : null;
		}
		case 'message':
			return `/channels/${reminder.guild_id ?? '@me'}/${reminder.channel_id}/${reminder.message_id}`;
		default:
			return null;
	}
}

/**
 * Goes to what a reminder is about. A mail opens in Emails: the route carries
 * `?open=<id>` like the Rail's hand-off, and the mail is selected right away
 * since the page does not read the parameter itself. False for a bare note.
 */
export function openReminder(reminder: KonvoReminder): boolean {
	const path = reminderPath(reminder);
	if (!path) return false;
	transitionTo(path);
	if (reminderSource(reminder) === 'mail') KonvoMail.select(reminder.mail_message_id ?? reminder.message_id);
	return true;
}

export interface MailReminderParams {
	/** The Graph message id. */
	messageId: string;
	subject?: string | null;
	/** Sender name (or address) shown on the reminder. */
	from?: string | null;
	remindAt: Date;
	note?: string;
}

class KonvoRemindersStore {
	reminders: Array<KonvoReminder> = [];
	fetched = false;
	fetching = false;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	async fetch(): Promise<void> {
		if (this.fetching) return;
		this.fetching = true;
		try {
			const response = await http.get<{reminders: Array<KonvoReminder>}>(Endpoints.KONVO_REMINDERS);
			runInAction(() => {
				this.reminders = response.body?.reminders ?? [];
				this.fetched = true;
			});
		} catch (error) {
			logger.warn('Failed to load reminders', error);
			runInAction(() => {
				this.fetched = true;
			});
		} finally {
			runInAction(() => {
				this.fetching = false;
			});
		}
	}

	async create(params: {channelId?: string; messageId?: string; remindAt: Date; note?: string}): Promise<void> {
		await http.post(Endpoints.KONVO_REMINDERS, {
			body: {
				channel_id: params.channelId,
				message_id: params.messageId,
				remind_at: params.remindAt.toISOString(),
				note: params.note || undefined,
			},
		});
		void this.fetch();
	}

	/** "Remind me" on a mail: the Assistant DMs a card that opens it; the mail stays where it is. */
	async createMail(params: MailReminderParams): Promise<{ok: boolean}> {
		try {
			const response = await http.post<{id?: string}>(Endpoints.KONVO_REMINDERS, {
				body: {
					remind_at: params.remindAt.toISOString(),
					note: params.note || undefined,
					mail: {
						message_id: params.messageId,
						subject: params.subject || undefined,
						from: params.from || undefined,
					},
				},
			});
			if (!response.body?.id) return {ok: false};
		} catch (error) {
			logger.warn('Failed to set mail reminder', error);
			return {ok: false};
		}
		void this.fetch();
		return {ok: true};
	}

	/** Sets the same reminder again (the undo after Done or Cancel). */
	async recreate(reminder: KonvoReminder): Promise<void> {
		const remindAt = new Date(reminder.remind_at);
		const note = reminder.note ?? undefined;
		if (reminderSource(reminder) === 'mail') {
			await this.createMail({
				messageId: reminder.mail_message_id ?? reminder.message_id,
				subject: reminder.snippet,
				from: reminder.author_name,
				remindAt,
				note,
			});
			return;
		}
		const aboutMessage = reminderSource(reminder) === 'message';
		await this.create({
			channelId: aboutMessage ? reminder.channel_id : undefined,
			messageId: aboutMessage ? reminder.message_id : undefined,
			remindAt,
			note,
		});
	}

	async cancel(reminderId: string): Promise<void> {
		await http.delete(Endpoints.KONVO_REMINDER(reminderId));
		runInAction(() => {
			this.reminders = this.reminders.filter((reminder) => reminder.id !== reminderId);
		});
	}

	/** Pending reminders pointing at a given message (for menu state). */
	forMessage(messageId: string): Array<KonvoReminder> {
		return this.reminders.filter((reminder) => reminder.message_id === messageId);
	}
}

const KonvoReminders = new KonvoRemindersStore();

/** "Remind me" on an Outlook mail; resolves {ok: false} instead of throwing. */
export function createMailReminder(params: MailReminderParams): Promise<{ok: boolean}> {
	return KonvoReminders.createMail(params);
}

export default KonvoReminders;
